import React from 'react';
import Select from 'react-select';
import { Box, Typography, Chip } from '@mui/material';
import { Category, CheckCircle } from '@mui/icons-material';
import { getLanguageText } from '../../utils/multilanguageUtils';
 

export const MultiSelectDropdown = ({
  options = [],
  selectedValues = [],
  onChange,
  placeholder = 'Select options...',
  maxLabelLength = 30,
  isDisabled = false,
  isClearable = true,
  isSearchable = true,
  label = '',
  currentLanguage = 'en', // Add currentLanguage prop with default
}) => {
  console.log('MultiSelectDropdown rendering', selectedValues);

  console.log('MultiSelectDropdown props:', {
    optionsCount: options.length,
    selectedValues: selectedValues,
    currentLanguage: currentLanguage,
    options: options.map(o => ({
      id: o._id || o.id,
      name: getLanguageText(o.name || o.label, currentLanguage).replace(/<br\s*\/?\>/gi, ''),
    })),
  });

  // Truncate text helper
  const truncateText = (text, maxLength) => {
    if (!text) return '';
    const s = String(text);
    return s.length > maxLength ? s.substring(0, maxLength) + '...' : s;
  };

  // Ensure selectedValues is always an array of strings
  const normalizedSelectedValues = React.useMemo(() => {
    if (!Array.isArray(selectedValues)) {
      return [];
    }
    return selectedValues.map(v => String(v).trim()).filter(v => v !== '');
  }, [selectedValues]);

  console.log('Normalized selected values:', normalizedSelectedValues);

  // Format options for react-select
  const formattedOptions = React.useMemo(() => {
    return options.map(option => {
      const optionId = String(option._id || option.id || option.value || '').trim();
      
      // Get the name/label in the current language
      const rawName = option.name || option.label || option.title || '';
      const languageSpecificName = getLanguageText(rawName, currentLanguage);
      
      // Remove HTML tags and clean up the text
      const optionName = languageSpecificName.replace(/<br\s*\/?\>/gi, '').trim() || 'Unnamed';

      return {
        value: optionId,
        label: truncateText(optionName, maxLabelLength),
        fullLabel: optionName,
        original: option,
      };
    });
  }, [options, maxLabelLength, currentLanguage]);

  console.log('Formatted options:', formattedOptions);

  // Get currently selected options
  const selectedOptions = React.useMemo(() => {
    const selected = formattedOptions.filter(option =>
      normalizedSelectedValues.includes(option.value)
    );
    return selected;
  }, [formattedOptions, normalizedSelectedValues]);

  // Handle selection change
  const handleChange = selectedOptions => {
    console.log('Selection changed:', selectedOptions);

    if (!selectedOptions) {
      onChange([]);
      return;
    }

    const selectedIds = selectedOptions.map(option => option.value);
    console.log('Selected IDs to send:', selectedIds);
    onChange(selectedIds);
  };

  // Custom styles for react-select
  const customStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '56px', // ↓ Reduce height
      // height: "56px",
      borderColor: state.isFocused ? '#1976d2' : '#c4c4c4',
      borderWidth: state.isFocused ? '2px' : '1px',
      borderRadius: '6px',
      boxShadow: state.isFocused ? '0 0 0 1px #1976d2' : 'none',
      '&:hover': {
        borderColor: '#1976d2',
      },
      backgroundColor: state.isDisabled ? '#f5f5f5' : '#fff',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.2s ease',
    }),

    valueContainer: base => ({
      ...base,
      padding: '2px 8px', // ↓ Reduce internal padding
      display: 'flex',
      alignItems: 'center',
      minHeight: '38px',
    }),

    input: base => ({
      ...base,
      margin: 0,
      padding: 0,
    }),

    singleValue: base => ({
      ...base,
      fontSize: '14px',
    }),

    placeholder: base => ({
      ...base,
      fontSize: '14px',
      color: '#888',
    }),

    dropdownIndicator: base => ({
      ...base,
      padding: '4px', // ↓ Smaller icon button
    }),

    clearIndicator: base => ({
      ...base,
      padding: '4px',
    }),

    multiValue: base => ({
      ...base,
      backgroundColor: '#e3f2fd',
      borderRadius: '4px',
      padding: '0 2px',
    }),

    multiValueLabel: base => ({
      ...base,
      fontSize: '12px',
      padding: '2px',
    }),

    multiValueRemove: base => ({
      ...base,
      padding: '2px',
      cursor: 'pointer',
      ':hover': {
        backgroundColor: '#1976d2',
        color: 'white',
      },
    }),

    menu: base => ({
      ...base,
      borderRadius: '6px',
      marginTop: '4px',
      zIndex: 9999,
    }),

    menuList: base => ({
      ...base,
      maxHeight: '180px', // scroll if large
    }),
  };

  // Custom format for option label with icon
  const formatOptionLabel = ({ label, fullLabel }, { context }) => {
    if (context === 'menu') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Category sx={{ fontSize: 18, color: '#1976d2' }} />
          <Typography variant="body2" sx={{ flex: 1 }} title={fullLabel}>
            {label}
          </Typography>
        </Box>
      );
    }
    return label;
  };

  return (
    <Box sx={{ width: '100%' }}>
      {label && (
        <Typography
          variant="subtitle2"
          sx={{
            mb: 1,
            fontWeight: 600,
            color: '#333',
            fontSize: '14px',
          }}
        >
          {label}
        </Typography>
      )}

      <Select
        isMulti
        options={formattedOptions}
        value={selectedOptions}
        onChange={handleChange}
        placeholder={placeholder}
        formatOptionLabel={formatOptionLabel}
        styles={customStyles}
        isDisabled={isDisabled}
        isClearable={isClearable}
        isSearchable={isSearchable}
        classNamePrefix="multi-select"
        noOptionsMessage={() => 'No options available'}
        loadingMessage={() => 'Loading...'}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        components={{
          IndicatorSeparator: () => null,
        }}
        getOptionLabel={option => option.fullLabel || option.label}
        getOptionValue={option => option.value}
      />

      {/* Debug info (remove in production) */}
      <Box sx={{ mt: 1, fontSize: '12px', color: '#666' }}>
        <Typography variant="caption">
          Options: {formattedOptions.length} | Selected: {selectedOptions.length}
        </Typography>
      </Box>

      {/* Selected count indicator */}
      {selectedOptions.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle sx={{ fontSize: 16, color: '#2e7d32' }} />
          <Typography variant="caption" sx={{ color: '#666' }}>
            {selectedOptions.length} item{selectedOptions.length !== 1 ? 's' : ''} selected
          </Typography>
        </Box>
      )}
    </Box>
  );
};
